import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Select, Spin } from "antd";
import PropTypes from "prop-types";

import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

const Component = ({ data = {}, onUpdate, options: { platforms } }) => {
	const initialState = { users: [] };
	const [state, setState] = useState(initialState);
	const { loading, submitting, visible } = state;
	const { id } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				const action =
					hash === `#${Modals.CREATE}`
						? API.version.create(values)
						: API.version.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.version
						.details(id)
						.then(({ data }) => {
							form.setFieldsValue({
								...data,
								platformId: data?.platform?.id,
							});

							setState((prevState) => ({
								...prevState,
								loading: false,
							}));
						})
						.catch(() => {
							setState((prevState) => ({
								...prevState,
								loading: false,
							}));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={
				<>
					<Button type="primary" onClick={handleSubmit} loading={submitting}>
						{Resources.confirm}
					</Button>
					<Button onClick={() => navigate(-1)} loading={submitting}>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={`${
				hash === `#${Modals.CREATE}` ? Resources.add : Resources.edit
			} ${Resources.version}`}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form layout="vertical" form={form}>
				{loading ? (
					<Spin className="sp-drawer-spin" />
				) : (
					<>
						<Form.Item
							name="title"
							label={Resources.title}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>

						<Form.Item
							name="platformId"
							label={Resources.platform}
							rules={Rules.required}
						>
							<Select options={platforms} />
						</Form.Item>
						<Form.Item
							name="forceUpdate"
							label={`${Resources.update} ${Resources.force}${Resources.y}`}
							rules={Rules.required}
						>
							<Select
								options={[
									{ value: true, label: `${Resources.force}${Resources.y}` },
									{
										value: false,
										label: `${Resources.else} ${Resources.force}${Resources.y}`,
									},
								]}
							/>
						</Form.Item>
						<Form.Item
							name="versionNumber"
							label={`${Resources.number} ${Resources.version}`}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="description"
							label={Resources.description}
							rules={Rules.required}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
};

export default Component;
